/**
 * Customer Intelligence — single, reusable per-client dashboard.
 *
 * Used in TWO places (per Dipali doctrine 26-Apr-2026):
 *   1. /client-intelligence/{clientId}            — CEO drill-down from the hub.
 *   2. Client Dossier → "Intelligence" tab        — Leader's per-client view.
 *
 * Both routes hit the SAME `/api/customer-intelligence/{clientId}` endpoint
 * so the dashboard is identical regardless of who's looking. CEO-only
 * actions (Sync now, Generate report) are gated by user role.
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { canAccessClientIntel } from '@/lib/permissions';
import api from '@/lib/api';
import {
  Activity, Users, MapPin, Award, Sparkles, RefreshCw, FileText,
  TrendingUp, Mail, MessageCircle, Layers, Clock, Inbox, Calendar,
  AlertCircle, Loader2, CheckCircle2,
} from 'lucide-react';
import SoulSheetsPanel from './SoulSheetsPanel';
import MembersTable from './MembersTable';
import NewslettersPanel from './NewslettersPanel';
import DeckPreview from './DeckPreview';
import { InfoTooltip } from './InfoTooltip';

const fmtN = (v) => (Number(v) || 0).toLocaleString('en-IN');
const fmtPct = (v) => `${(Number(v) || 0).toFixed(1)}%`;

const TIER_DOT = { RR: '#1C304A', RRS: '#5C5652', Unknown: '#B0B7BB' };

export default function CustomerIntelligenceDashboard({ clientId, hideHeader = false }) {
  const { user } = useAuth();
  const isCeo = user?.role === 'ceo';
  // Newsletter editing is open to CEO + Concierge® team (Nisha, Samar,
  // Shiva, Tanisha) per Dipali doctrine 30-Apr-2026 — Tanisha is the
  // designated newsletter operator, the rest review and approve.
  const canEditNewsletters = canAccessClientIntel(user);
  const [pack, setPack] = useState(null);
  const [breakdowns, setBreakdowns] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState(null);
  // CSV ingestion (CEO only)
  const [csvBusy, setCsvBusy] = useState(false);
  const [csvMsg, setCsvMsg] = useState(null);
  const [ticketsFile, setTicketsFile] = useState(null);
  const [contactsFile, setContactsFile] = useState(null);
  // Layer 1 — Live Tickets feed
  const [tickets, setTickets] = useState([]);
  const [ticketsTotal, setTicketsTotal] = useState(0);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [ticketLimit, setTicketLimit] = useState(50);
  const [ticketOffset, setTicketOffset] = useState(0);
  const [ticketStatus, setTicketStatus] = useState('');
  // Date range filter — defaults empty (= "all dates"); quick-chip presets set both at once.
  const [ticketStart, setTicketStart] = useState('');
  const [ticketEnd, setTicketEnd] = useState('');
  // Layer 3 — Report Generator date range
  const [reportStart, setReportStart] = useState('');
  const [reportEnd, setReportEnd] = useState('');
  const [reportMsg, setReportMsg] = useState(null);
  const [reportBusy, setReportBusy] = useState(false);
  // Phase 5/6 — Deck generator (Claude narratives + 27-slide PPTX)
  const [deckMode, setDeckMode] = useState('month'); // 'month' | 'range'
  // Top-level tab — 'intelligence' (existing dashboard) or 'soul' (new Soul Engine)
  const [activeTab, setActiveTab] = useState('members');
  const [deckMonth, setDeckMonth] = useState(() => {
    const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - 1);
    return d.toISOString().slice(0, 7);
  });
  const [deckStart, setDeckStart] = useState('');
  const [deckEnd, setDeckEnd] = useState('');
  const [deckBusy, setDeckBusy] = useState(false);
  const [deckMsg, setDeckMsg] = useState(null);
  const [qaBusy, setQaBusy] = useState(false);
  const [qaResult, setQaResult] = useState(null);
  // Deck Preview modal — when true, renders <DeckPreview /> full-screen.
  // Opens with Claude drafting all narratives, lets Dipali edit/rewrite
  // each section, then finalises to PPT on demand.
  const [showPreview, setShowPreview] = useState(false);
  // After clicking "Generate Report" from the header we need to switch
  // to the Dashboard tab AND scroll to the deck generator once it
  // renders. useState + useEffect lets us do it cleanly.
  const [scrollToDeck, setScrollToDeck] = useState(false);
  // Recent decks table (for re-download without re-generating)
  const [recentDecks, setRecentDecks] = useState([]);
  // MCE effectiveness — tracked separately so it NEVER inflates the
  // engagement numbers in the client deck. Per Dipali 30-Apr-2026.
  const [mceEff, setMceEff] = useState(null);

  useEffect(() => {
    if (!scrollToDeck) return;
    // Wait one tick for the tab content to mount, then scroll.
    const t = setTimeout(() => {
      const el = document.getElementById('ci-deck-generator');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        // Subtle highlight pulse so the user's eye lands on the panel
        el.style.boxShadow = '0 0 0 3px #E5C9A0';
        setTimeout(() => { el.style.boxShadow = ''; }, 1800);
      }
      setScrollToDeck(false);
    }, 80);
    return () => clearTimeout(t);
  }, [scrollToDeck, activeTab]);

  const openDeckGenerator = () => {
    setActiveTab('intelligence');
    setScrollToDeck(true);
  };

  const loadRecentDecks = async () => {
    try {
      const r = await api.get(`/customer-intelligence/${clientId}/deck/recent`,
                               { params: { limit: 10 } });
      setRecentDecks(r.data?.decks || []);
    } catch {
      setRecentDecks([]);
    }
  };
  useEffect(() => { loadRecentDecks(); /* eslint-disable-next-line */ }, [clientId]);

  const loadMceEff = async () => {
    try {
      const r = await api.get(`/customer-intelligence/${clientId}/mce-effectiveness`,
                               { params: { lookback: 6 } });
      setMceEff(r.data);
    } catch {
      setMceEff(null);
    }
  };
  useEffect(() => { loadMceEff(); /* eslint-disable-next-line */ }, [clientId]);

  // ─── MCE backfill · self-healing banner ──────────────────────────
  // When the CI dashboard loads, we silently check whether any
  // historical tickets carry the MCE fingerprint (pillar="Member Care"
  // OR benefit="Engagement Tracking") but lack the `is_mce_outreach`
  // flag. If so, we render an amber banner offering a one-click fix.
  //
  // Why this exists: sync-time tagging only works for tickets synced
  // AFTER the fix shipped (02-May-2026). Historical tickets (~491 on
  // JLR April) need a one-time backfill or they inflate engagement
  // metrics. Once clicked, the banner disappears forever.
  const [backfillCount, setBackfillCount] = useState(null); // null = not checked, 0 = clean
  const [backfillBreakdown, setBackfillBreakdown] = useState({ mce: 0, soul: 0 });
  const [backfillBusy, setBackfillBusy] = useState(false);
  const [backfillDone, setBackfillDone] = useState(null);

  const checkBackfill = async () => {
    if (!isCeo) return; // CEO-only endpoint
    try {
      const r = await api.post(
        `/customer-intelligence/_admin/backfill-mce-tags`,
        null, { params: { client_id: clientId, dry_run: true } },
      );
      setBackfillCount(r.data?.matched || 0);
      setBackfillBreakdown({
        mce: r.data?.mce_matched || 0,
        soul: r.data?.soul_matched || 0,
      });
    } catch {
      setBackfillCount(0); // don't annoy the user if the check itself fails
    }
  };
  useEffect(() => { checkBackfill(); /* eslint-disable-next-line */ }, [clientId, isCeo]);

  const runBackfill = async () => {
    const detail = backfillBreakdown.soul > 0
      ? `Tag ${backfillCount} historical internal artefacts?\n\n` +
         `· ${backfillBreakdown.mce} MCE outreach nudges\n` +
         `· ${backfillBreakdown.soul} Soul Sheets\n\n`
      : `Tag ${backfillCount} historical MCE tickets as outreach?\n\n`;
    if (!window.confirm(
      detail +
      `This is safe and reversible. After this, engagement metrics ` +
      `and the April deck will show the honest inbound numbers, ` +
      `and the internal artefacts will appear in their own Proactive ` +
      `Care / Soul Engine sections.`)) return;
    setBackfillBusy(true);
    try {
      const r = await api.post(
        `/customer-intelligence/_admin/backfill-mce-tags`,
        null, { params: { client_id: clientId, dry_run: false } },
      );
      setBackfillDone({
        ok: true,
        modified: r.data?.modified || 0,
        at: new Date().toISOString(),
      });
      setBackfillCount(0); // banner disappears
      // Refresh all the dashboard stats that were affected
      load();
      loadMceEff();
    } catch (e) {
      setBackfillDone({
        ok: false,
        error: e?.response?.data?.detail || e.message,
      });
    } finally {
      setBackfillBusy(false);
    }
  };

  const downloadRecentDeck = async (job) => {
    try {
      const dl = await api.get(
        `/customer-intelligence/${clientId}/deck/download/${job.job_id}`,
        { responseType: 'blob', timeout: 60000 },
      );
      const blob = new Blob([dl.data], {
        type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      });
      const url = URL.createObjectURL(blob);
      const slug = job.month || `${job.start}_to_${job.end}`;
      const fname = `${clientId}_${slug}_member_experience.pptx`;
      const a = document.createElement('a');
      a.href = url; a.download = fname;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(`Download failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const r = await api.get(`/customer-intelligence/${clientId}`);
      setPack(r.data);
      // Fire-and-forget the tooltip-breakdown fetch — not blocking first paint.
      // Used by hover tooltips on each stat card.
      api.get(`/customer-intelligence/${clientId}/tooltip-breakdowns`)
        .then(res => setBreakdowns(res.data))
        .catch(() => {}); // non-fatal — tooltips just won't load
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load Customer Intelligence');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId]);

  const loadTickets = async () => {
    setTicketsLoading(true);
    try {
      const params = { limit: ticketLimit, offset: ticketOffset };
      if (ticketStatus) params.status = ticketStatus;
      if (ticketStart) params.start = ticketStart;
      if (ticketEnd) params.end = ticketEnd;
      const r = await api.get(`/customer-intelligence/${clientId}/recent-tickets`, { params });
      setTickets(r.data?.tickets || []);
      setTicketsTotal(r.data?.total || 0);
    } catch {
      setTickets([]);
      setTicketsTotal(0);
    } finally { setTicketsLoading(false); }
  };
  useEffect(() => { loadTickets(); /* eslint-disable-next-line */ },
    [clientId, ticketLimit, ticketStatus, ticketStart, ticketEnd, ticketOffset]);
  // Reset to page 1 whenever the filters change
  useEffect(() => { setTicketOffset(0); /* eslint-disable-next-line */ },
    [ticketLimit, ticketStatus, ticketStart, ticketEnd, clientId]);

  const generateReport = async () => {
    setReportBusy(true); setReportMsg(null);
    try {
      const params = {};
      if (reportStart) params.start = reportStart;
      if (reportEnd) params.end = reportEnd;
      const r = await api.post(`/customer-intelligence/${clientId}/generate-report`, null, { params });
      const s = r.data?.snapshot || {};
      setReportMsg({
        ok: true,
        text: `${r.data?.message || 'OK'} · Snapshot: ${s.active_members || 0}/${s.total_members || 0} active (${(s.active_pct || 0).toFixed(1)}%) · top pillar ${s.top_pillar || '—'} · ${s.requests_in_anchor_month || 0} reqs`,
      });
    } catch (e) {
      setReportMsg({ ok: false, text: e?.response?.data?.detail || e.message });
    } finally { setReportBusy(false); }
  };

  const triggerSync = async (params) => {
    setSyncing(true); setSyncMsg({ ok: true, text: '⏳ Sync queued — polling for completion (this can take 30-120s for large pulls)…' });
    try {
      const r = await api.post(`/customer-intelligence/${clientId}/sync`, null, { params });
      const runId = r.data?.run_id;

      // 07-Feb-2026: backend now runs sync in background to bypass
      // Cloudflare's 100s proxy timeout. Poll /_meta/sync-runs every 5s
      // until our run finishes (or 10 min timeout).
      const pollUntil = Date.now() + 10 * 60 * 1000;
      let final = null;
      while (Date.now() < pollUntil) {
        await new Promise(res => setTimeout(res, 5000));
        try {
          const rr = await api.get(`/customer-intelligence/_meta/sync-runs`, { params: { limit: 30 } });
          const runs = rr.data?.runs || [];
          const ours = runId ? runs.find(x => x.run_id === runId) : runs[0];
          if (ours && ours.ok !== null && ours.ok !== undefined) {
            final = ours;
            break;
          }
        } catch (_) { /* keep polling */ }
      }
      if (!final) {
        setSyncMsg({ ok: false, text: '⚠ Sync still running after 10 min. Refresh the page later to see results.' });
      } else if (final.ok === false) {
        setSyncMsg({ ok: false, text: `❌ ${final.error || (final.result || {}).error || 'Sync failed'}` });
      } else {
        const t = final.tickets_upserted ?? (final.result || {}).tickets_upserted ?? (final.result?.results?.[0]?.tickets_upserted) ?? 0;
        const c = final.contacts_upserted ?? (final.result || {}).contacts_upserted ?? (final.result?.results?.[0]?.contacts_upserted) ?? 0;
        const dur = final.duration_s ? ` in ${final.duration_s.toFixed(1)}s` : '';
        setSyncMsg({ ok: true, text: `✅ Pulled ${t} tickets, ${c} contacts${dur}` });
        await load();
      }
    } catch (e) {
      setSyncMsg({ ok: false, text: `❌ ${e?.response?.data?.detail || e.message}` });
    } finally { setSyncing(false); }
  };

  const triggerCrmSync = async () => {
    setSyncing(true); setSyncMsg({ ok: true, text: '⏳ Pulling members from Zoho CRM (~15-25s)…' });
    try {
      const r = await api.post(`/soul-engine/${clientId}/crm-sync`, null, { timeout: 90000 });
      const d = r.data;
      setSyncMsg({
        ok: true,
        text: `✅ Synced from CRM · ${d.total_upserted} members (${d.inserted} new, ${d.updated} updated) in ${(d.duration_s||0).toFixed(1)}s`,
      });
      await load();
    } catch (e) {
      setSyncMsg({ ok: false, text: `❌ ${e?.response?.data?.detail || e.message}` });
    } finally { setSyncing(false); }
  };

  const ingestCsvs = async () => {
    if (!ticketsFile && !contactsFile) {
      setCsvMsg({ ok: false, text: 'Pick at least one CSV (tickets and/or contacts).' });
      return;
    }
    setCsvBusy(true); setCsvMsg(null);
    try {
      const fd = new FormData();
      if (ticketsFile) fd.append('tickets_csv', ticketsFile);
      if (contactsFile) fd.append('contacts_csv', contactsFile);
      const r = await api.post(`/customer-intelligence/${clientId}/ingest-csvs`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const tk = r.data?.tickets || {};
      const cc = r.data?.contacts || {};
      setCsvMsg({
        ok: true,
        text: `✅ Tickets matched ${tk.rows_for_client - tk.rows_unmatched_in_db}/${tk.rows_for_client} · updated ${tk.rows_updated} · contacts updated ${cc.contacts_updated} (+${cc.contacts_inserted} new)`,
        detail: r.data,
      });
      await load();
      await loadTickets();
    } catch (e) {
      setCsvMsg({ ok: false, text: `❌ ${e?.response?.data?.detail || e.message}` });
    } finally { setCsvBusy(false); }
  };

  const _deckPeriodParams = () => {
    if (deckMode === 'range') {
      if (!deckStart || !deckEnd) return null;
      return { start: deckStart, end: deckEnd };
    }
    if (!deckMonth) return null;
    return { month: deckMonth };
  };

  const runQa = async () => {
    const params = _deckPeriodParams();
    if (!params) { setQaResult({ overall: 'red', error: 'Pick a period first.' }); return; }
    setQaBusy(true); setQaResult(null);
    try {
      const r = await api.get(`/customer-intelligence/${clientId}/qa`, { params });
      setQaResult(r.data);
    } catch (e) {
      setQaResult({ overall: 'red', error: e?.response?.data?.detail || e.message });
    } finally { setQaBusy(false); }
  };

  const generateDeck = async () => {
    const params = _deckPeriodParams();
    if (!params) {
      setDeckMsg({ ok: false, text: 'Pick a month or a date range first.' });
      return;
    }
    setDeckBusy(true); setDeckMsg({ ok: true, text: 'Kicking off deck job…' });
    try {
      // Step 1 — start the job. Returns immediately with job_id; the heavy
      // work runs in a background task on the backend.
      const r = await api.post(`/customer-intelligence/${clientId}/deck`, null, {
        params, timeout: 30000,
      });
      const job_id = r.data?.job_id;
      if (!job_id) throw new Error('No job_id returned');
      setDeckMsg({ ok: true, text: `Job ${job_id.slice(0, 8)} started · generating narratives…` });

      // Step 2 — poll status every 6s, up to 5 minutes
      const POLL_MAX = 50; // 50 * 6s = 5 min
      let ready = null;
      for (let i = 0; i < POLL_MAX; i++) {
        await new Promise(r => setTimeout(r, 6000));
        const s = await api.get(`/customer-intelligence/${clientId}/deck/status/${job_id}`);
        const elapsed = Math.round(s.data?.elapsed_s || 0);
        if (s.data?.status === 'ready') { ready = s.data; break; }
        if (s.data?.status === 'failed') {
          setDeckMsg({ ok: false, text: `❌ Job failed · ${s.data.error || 'unknown error'}` });
          setDeckBusy(false); return;
        }
        // Path B (Dipali · 05-Feb-2026): show the per-step `progress` label
        // ("loading intelligence pack", "generating Claude narratives (22 pillars)",
        //  "assembling 27-slide PPTX", "uploading PPTX to CDN", ...).
        const progress = s.data?.progress;
        const pills = s.data?.pillars_used?.length || 0;
        const stepLabel = progress && progress !== s.data?.status
          ? progress
          : `${pills} pillars done`;
        setDeckMsg({ ok: true, text: `Generating… ${elapsed}s · ${stepLabel}` });
      }
      if (!ready) {
        setDeckMsg({ ok: false, text: '⏳ Job is taking longer than 5 min. It will finish in the background — refresh the page in a few minutes and the deck will be in the Recent Decks list.' });
        setDeckBusy(false); return;
      }

      // Step 3 — download the file. Path B doctrine (Dipali · 05-Feb-2026):
      // if the worker uploaded to Cloudinary (`cdn_url` present), open
      // it directly. The CDN bypasses Cloudflare's 100s ceiling that
      // was 520'ing large-deck downloads. Legacy jobs without a CDN URL
      // still stream from the backend.
      const slug = deckMode === 'month' ? deckMonth : `${deckStart}_to_${deckEnd}`;
      const fname = `${clientId}_${slug}_member_experience.pptx`;
      let downloadedBytes = ready.size_bytes;
      if (ready.cdn_url) {
        const a = document.createElement('a');
        a.href = ready.cdn_url; a.download = fname; a.target = '_blank';
        document.body.appendChild(a); a.click(); a.remove();
      } else {
        const dl = await api.get(`/customer-intelligence/${clientId}/deck/download/${job_id}`, {
          responseType: 'blob', timeout: 60000,
        });
        const blob = new Blob([dl.data], {
          type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        });
        downloadedBytes = blob.size;
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = fname;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
      }
      setDeckMsg({
        ok: true,
        text: `✅ ${fname} downloaded · ${(downloadedBytes/1024).toFixed(0)} KB · ${ready.pillars_used?.length || 0} pillars · ${Math.round(ready.duration_s || 0)}s${ready.cdn_url ? ' · email sent to Dipali + Nisha' : ''}`,
      });
      // Refresh the recent-decks table so the new row appears immediately.
      loadRecentDecks();
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      setDeckMsg({ ok: false, text: `❌ ${detail}` });
    } finally { setDeckBusy(false); }
  };

  if (loading) return <div className="text-center text-sm py-8" style={{ color: '#9CA3AF' }}>Loading Customer Intelligence…</div>;
  if (error)   return <div className="text-sm py-4" style={{ color: '#A32D2D' }} data-testid="ci-error">{error}</div>;
  if (!pack)   return null;

  const a = pack.activation;
  const p = pack.pillar_summary;
  const b = pack.benefit_summary || { rows: [], months: [], monthly_totals: {}, grand_total: 0, coverage_pct: 0, top_benefit: null };
  const g = pack.geography;
  const t = pack.tier;
  const ch = pack.channel;
  const tr = pack.monthly_trend;

  return (
    <div className="space-y-5" data-testid={`ci-dashboard-${clientId}`}>
      {!hideHeader && (
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="text-[10px] tracking-[0.18em] font-bold" style={{ color: '#2A7B88' }}>CUSTOMER INTELLIGENCE</div>
            <h2 className="text-xl font-bold" style={{ color: '#1B2A4A' }}>{clientId.toUpperCase()} · {pack.anchor_month}</h2>
            <ReconciliationBadge clientId={clientId} />
            {pack.last_sync && (
              <SyncStatusPill
                lastSync={pack.last_sync}
                clientId={clientId}
              />
            )}
          </div>
          {isCeo && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => triggerSync({})}
                disabled={syncing}
                data-testid="ci-sync-now"
                className="px-3 py-1.5 rounded-md text-[11px] font-medium border hover:bg-gray-50 inline-flex items-center gap-1 disabled:opacity-50"
                style={{ borderColor: '#E5E1DC', color: '#1B2A4A' }}>
                <RefreshCw size={12} className={syncing ? 'animate-spin' : ''} /> {syncing ? 'Syncing…' : 'Sync now'}
              </button>
              <button
                onClick={triggerCrmSync}
                disabled={syncing}
                data-testid="ci-sync-crm"
                title="Pull the full member roster from Zoho CRM (replaces the monthly CSV)"
                className="px-3 py-1.5 rounded-md text-[11px] font-medium text-white inline-flex items-center gap-1 disabled:opacity-50"
                style={{ backgroundColor: '#2A7B88' }}>
                <Sparkles size={12} /> Sync from CRM
              </button>
              <a
                href="/admin/ci"
                data-testid="ci-admin-link"
                title="Customer Intelligence Admin — Health · Field Mappings · Members"
                className="px-3 py-1.5 rounded-md text-[11px] font-medium border hover:bg-gray-50 inline-flex items-center gap-1"
                style={{ borderColor: '#1C304A', color: '#1B2A4A' }}>
                Admin →
              </a>
              <button
                onClick={openDeckGenerator}
                data-testid="ci-generate-report"
                title="Open the 27-slide Member Experience deck generator"
                className="px-3 py-1.5 rounded-md text-[11px] font-medium text-white inline-flex items-center gap-1 hover:opacity-90"
                style={{ backgroundColor: '#1C304A' }}>
                <FileText size={12} /> Generate Report
              </button>
            </div>
          )}
        </div>
      )}

      {syncMsg && (
        <div className="text-[12px] px-3 py-2 rounded-md" style={{ background: syncMsg.ok ? '#ECFDF5' : '#FEE2E2', color: syncMsg.ok ? '#1f5736' : '#7a1f1f' }}>
          {syncMsg.text}
        </div>
      )}

      {/* Top-level tabs — MEMBERS leads (the primary workspace), then
          Soul Sheets, then the analytics Dashboard. */}
      <div className="flex items-center gap-1 border-b" style={{ borderColor: '#E5E1DC' }} data-testid="ci-tabs">
        <TabButton
          active={activeTab === 'members'}
          onClick={() => setActiveTab('members')}
          testId="ci-tab-members"
          icon={<Users size={12} />}>
          Members
        </TabButton>
        <TabButton
          active={activeTab === 'soul'}
          onClick={() => setActiveTab('soul')}
          testId="ci-tab-soul"
          icon={<Sparkles size={12} />}>
          Soul Sheets
        </TabButton>
        <TabButton
          active={activeTab === 'intelligence'}
          onClick={() => setActiveTab('intelligence')}
          testId="ci-tab-intelligence"
          icon={<Activity size={12} />}>
          Dashboard
        </TabButton>
        <TabButton
          active={activeTab === 'newsletters'}
          onClick={() => setActiveTab('newsletters')}
          testId="ci-tab-newsletters"
          icon={<Mail size={12} />}>
          Newsletters
        </TabButton>
      </div>

      {activeTab === 'members' && (
        <MembersTable clientId={clientId} isCeo={isCeo} />
      )}

      {activeTab === 'soul' && (
        <SoulSheetsPanel clientId={clientId} isCeo={isCeo} />
      )}

      {activeTab === 'newsletters' && (
        <NewslettersPanel clientId={clientId} canEdit={canEditNewsletters} />
      )}

      {activeTab === 'intelligence' && (<>

      {/* CEO-only quick backfill controls */}
      {isCeo && (
        <div className="flex flex-wrap gap-2 items-center text-[11px] p-2 rounded-md" style={{ background: '#F7F5F2' }}>
          <span style={{ color: '#6B7280' }}>Phased backfill:</span>
          <button onClick={() => triggerSync({ from_date: '2026-04-01' })} disabled={syncing} className="px-2 py-1 rounded border hover:bg-white" style={{ borderColor: '#E5E1DC' }} data-testid="ci-backfill-A">A · Apr 2026</button>
          <button onClick={() => triggerSync({ from_date: '2026-01-01' })} disabled={syncing} className="px-2 py-1 rounded border hover:bg-white" style={{ borderColor: '#E5E1DC' }} data-testid="ci-backfill-B">B · Jan-Mar 2026</button>
          <button onClick={() => triggerSync({ from_date: '2025-06-01' })} disabled={syncing} className="px-2 py-1 rounded border hover:bg-white" style={{ borderColor: '#E5E1DC' }} data-testid="ci-backfill-C">C · Jun-Dec 2025</button>
        </div>
      )}

      {/* CEO-only CSV enrichment uploader. Used to fill data gaps the
         Zoho API doesn't expose cleanly (e.g. JLR car model, Residence
         City). Match keys: ticket_number for tickets, member_contact_id
         (bridged via tickets) for contacts. Idempotent. */}
      {isCeo && (
        <div className="text-[11px] p-3 rounded-md space-y-2" style={{ background: '#FFF8EE', border: '1px solid #F4D8A8' }} data-testid="ci-csv-ingest">
          <div className="flex items-center gap-2 font-medium" style={{ color: '#8A4B00' }}>
            <FileText size={12} /> CSV enrichment (fill Zoho-API gaps)
          </div>
          <div className="flex flex-wrap items-center gap-3" style={{ color: '#6B5439' }}>
            <label className="inline-flex items-center gap-1">
              <span>Tickets CSV</span>
              <input
                type="file"
                accept=".csv"
                data-testid="ci-csv-tickets"
                onChange={(e) => setTicketsFile(e.target.files?.[0] || null)}
                className="text-[11px]"
              />
            </label>
            <label className="inline-flex items-center gap-1">
              <span>Contacts CSV</span>
              <input
                type="file"
                accept=".csv"
                data-testid="ci-csv-contacts"
                onChange={(e) => setContactsFile(e.target.files?.[0] || null)}
                className="text-[11px]"
              />
            </label>
            <button
              onClick={ingestCsvs}
              disabled={csvBusy || (!ticketsFile && !contactsFile)}
              data-testid="ci-csv-ingest-btn"
              className="px-3 py-1 rounded text-white text-[11px] font-medium disabled:opacity-50"
              style={{ background: '#8A4B00' }}>
              {csvBusy ? 'Ingesting…' : 'Ingest CSVs'}
            </button>
          </div>
          {csvMsg && (
            <div className="text-[11px]" style={{ color: csvMsg.ok ? '#1f5736' : '#7a1f1f' }} data-testid="ci-csv-msg">
              {csvMsg.text}
              {csvMsg.detail?.contacts?.model_distribution && Object.keys(csvMsg.detail.contacts.model_distribution).length > 0 && (
                <div className="mt-1 text-[10px]" style={{ color: '#6B5439' }}>
                  Models: {Object.entries(csvMsg.detail.contacts.model_distribution).map(([m, n]) => `${m} (${n})`).join(' · ')}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* MCE Backfill banner — self-healing one-time fix for historical
         tickets that carry the MCE fingerprint but lack the
         `is_mce_outreach` flag. Appears only when count > 0, then
         disappears forever after one click. */}
      {isCeo && backfillCount !== null && backfillCount > 0 && (
        <div
          className="text-[11px] p-3 rounded-md flex items-start gap-2"
          style={{
            background: '#FFF8E1', border: '1px solid #F4C430',
            color: '#8A5B00',
          }}
          data-testid="ci-backfill-banner">
          <AlertCircle size={14} className="shrink-0 mt-0.5" style={{ color: '#B8860B' }} />
          <div className="flex-1 space-y-1.5">
            <div>
              <strong>{backfillCount.toLocaleString()}</strong> historical
              tickets need to be tagged as internal artefacts
              {backfillBreakdown.mce > 0 && backfillBreakdown.soul > 0
                ? ` · ${backfillBreakdown.mce} MCE outreach + ${backfillBreakdown.soul} Soul Sheets`
                : backfillBreakdown.mce > 0
                  ? ` · MCE outreach (birthday / dormant / welcome notes)`
                  : ` · Soul Sheets produced by the Soul Engine`}.
              Without this, your engagement metrics and the April deck
              will over-count member requests by a large margin (per
              Nisha's 30-Apr-2026 Zoho export, JLR real engagement is
              ~273 requests but these artefacts inflate the total to 1,571).
            </div>
            <div style={{ color: '#6B4E00' }}>
              One-time fix · safe · takes ~10 seconds. After this, engagement
              numbers will be honest, MCE outreach will appear separately in
              the Proactive Care section, and Soul Sheets will have their
              own "Member Intelligence Produced" metric.
            </div>
            <button
              onClick={runBackfill}
              disabled={backfillBusy}
              data-testid="ci-backfill-run"
              className="text-[11px] px-3 py-1.5 rounded font-medium disabled:opacity-60 hover:opacity-90 inline-flex items-center gap-1.5"
              style={{ background: '#1B2A4A', color: '#FFFFFF' }}>
              {backfillBusy
                ? <><Loader2 size={12} className="animate-spin" /> Tagging…</>
                : <><CheckCircle2 size={12} /> Fix now · tag {backfillCount} tickets</>}
            </button>
          </div>
        </div>
      )}

      {/* Success toast after backfill runs — auto-hides on next load */}
      {isCeo && backfillDone?.ok && (
        <div
          className="text-[11px] p-2.5 rounded-md flex items-center gap-2"
          style={{ background: '#ECFDF5', border: '1px solid #86EFAC',
                    color: '#1f5736' }}
          data-testid="ci-backfill-success">
          <CheckCircle2 size={13} />
          <span>
            <strong>{backfillDone.modified.toLocaleString()}</strong> historical
            MCE tickets tagged. Engagement metrics are now honest, and the
            deck will report MCE separately in Proactive Care.
          </span>
        </div>
      )}
      {isCeo && backfillDone?.ok === false && (
        <div
          className="text-[11px] p-2.5 rounded-md"
          style={{ background: '#FEF2F2', border: '1px solid #FCA5A5',
                    color: '#7F1D1D' }}
          data-testid="ci-backfill-error">
          Backfill failed: {backfillDone.error}
        </div>
      )}

      {/* CEO-only Deck Generator (Phase 5/6 — Claude narratives + 27-slide PPTX). */}
      {isCeo && (
        <div className="text-[11px] p-3 rounded-md space-y-2.5"
             style={{ background: '#1B2A4A', color: '#F7F5F2' }}
             data-testid="ci-deck-generator">
          <div className="flex items-center gap-2 font-medium" style={{ color: '#E5C9A0' }}>
            <FileText size={12} /> Member Experience Deck — Phase 5 + 6
          </div>
          <div className="text-[10.5px]" style={{ color: '#B7C2D8' }}>
            Pick a calendar month, or a custom date range — Claude writes pillar narratives and the 27-slide PPTX downloads when ready (~30–90s).
          </div>

          {/* Fast preset chips — Apr 2026 / Mar / Q1 / H2 2025 / Since Jun 2025 */}
          <div className="flex flex-wrap gap-1" data-testid="ci-deck-presets">
            {[
              { label: 'Apr 2026', mode: 'month', month: '2026-04' },
              { label: 'Mar 2026', mode: 'month', month: '2026-03' },
              { label: 'Feb 2026', mode: 'month', month: '2026-02' },
              { label: 'Q1 2026',  mode: 'range', start: '2026-01-01', end: '2026-03-31' },
              { label: 'H2 2025',  mode: 'range', start: '2025-07-01', end: '2025-12-31' },
              { label: 'Since Jun 2025', mode: 'range', start: '2025-06-01', end: '2026-04-30' },
            ].map(p => {
              const active = (p.mode === 'month' && deckMode === 'month' && deckMonth === p.month)
                || (p.mode === 'range' && deckMode === 'range' && deckStart === p.start && deckEnd === p.end);
              return (
                <button key={p.label}
                  data-testid={`ci-deck-preset-${p.label.replace(/\s/g, '-').toLowerCase()}`}
                  onClick={() => {
                    if (p.mode === 'month') {
                      setDeckMode('month');
                      setDeckMonth(p.month);
                    } else {
                      setDeckMode('range');
                      setDeckStart(p.start); setDeckEnd(p.end);
                    }
                  }}
                  className="text-[10.5px] px-2 py-0.5 rounded border hover:opacity-90"
                  style={{
                    background: active ? '#E5C9A0' : 'transparent',
                    color: active ? '#1B2A4A' : '#F7F5F2',
                    borderColor: active ? '#E5C9A0' : '#3A4A6A',
                  }}>
                  {p.label}
                </button>
              );
            })}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="inline-flex rounded overflow-hidden border" style={{ borderColor: '#3A4A6A' }}>
              <button
                onClick={() => setDeckMode('month')}
                data-testid="ci-deck-mode-month"
                className="px-2.5 py-1 text-[11px]"
                style={{ background: deckMode === 'month' ? '#E5C9A0' : 'transparent',
                         color: deckMode === 'month' ? '#1B2A4A' : '#F7F5F2' }}>Month</button>
              <button
                onClick={() => setDeckMode('range')}
                data-testid="ci-deck-mode-range"
                className="px-2.5 py-1 text-[11px]"
                style={{ background: deckMode === 'range' ? '#E5C9A0' : 'transparent',
                         color: deckMode === 'range' ? '#1B2A4A' : '#F7F5F2' }}>Custom range</button>
            </div>
            {deckMode === 'month' ? (
              <input
                type="month"
                value={deckMonth}
                onChange={(e) => setDeckMonth(e.target.value)}
                data-testid="ci-deck-month"
                className="text-[11px] px-2 py-1 rounded"
                style={{ background: '#0F1A2D', color: '#F7F5F2', border: '1px solid #3A4A6A' }}
              />
            ) : (
              <>
                <input
                  type="date"
                  value={deckStart}
                  onChange={(e) => setDeckStart(e.target.value)}
                  data-testid="ci-deck-start"
                  className="text-[11px] px-2 py-1 rounded"
                  style={{ background: '#0F1A2D', color: '#F7F5F2', border: '1px solid #3A4A6A' }}
                />
                <span style={{ color: '#9CA3AF' }}>→</span>
                <input
                  type="date"
                  value={deckEnd}
                  onChange={(e) => setDeckEnd(e.target.value)}
                  data-testid="ci-deck-end"
                  className="text-[11px] px-2 py-1 rounded"
                  style={{ background: '#0F1A2D', color: '#F7F5F2', border: '1px solid #3A4A6A' }}
                />
              </>
            )}
            <button
              onClick={runQa}
              disabled={qaBusy}
              data-testid="ci-deck-qa-btn"
              className="px-2.5 py-1 rounded text-[11px] font-medium border disabled:opacity-50"
              style={{ borderColor: '#3A4A6A', color: '#F7F5F2' }}>
              {qaBusy ? 'Checking…' : 'Run QA check'}
            </button>
            <button
              onClick={() => setShowPreview(true)}
              disabled={deckBusy}
              data-testid="ci-deck-preview-btn"
              title="Draft all narratives with Claude, edit inline, then generate the final PPT"
              className="px-3 py-1 rounded text-[11px] font-medium disabled:opacity-50 hover:opacity-90"
              style={{ background: '#E5C9A0', color: '#1B2A4A' }}>
              Preview &amp; generate
            </button>
            <button
              onClick={generateDeck}
              disabled={deckBusy}
              data-testid="ci-deck-generate-btn"
              title="Skip the preview — generate the 27-slide deck directly"
              className="px-3 py-1 rounded text-[11px] font-medium disabled:opacity-50 border hover:opacity-90"
              style={{ background: 'transparent', color: '#F7F5F2',
                        borderColor: '#3A4A6A' }}>
              {deckBusy ? 'Generating…' : 'Direct generate'}
            </button>
          </div>

          {qaResult && (
            <div data-testid="ci-deck-qa-result"
                 className="text-[11px] mt-1 p-2.5 rounded space-y-1"
                 style={{
                   background: qaResult.overall === 'green' ? '#0F2C1F' :
                                qaResult.overall === 'amber' ? '#332B0E' : '#3A1212',
                   border: '1px solid ' + (qaResult.overall === 'green' ? '#2A7B5A' :
                                            qaResult.overall === 'amber' ? '#8A6B0E' : '#7A1F1F'),
                 }}>
              {qaResult.error ? (
                <div style={{ color: '#FCA5A5' }}>{qaResult.error}</div>
              ) : (
                <>
                  <div className="font-medium" style={{
                    color: qaResult.overall === 'green' ? '#A0E5C2' :
                           qaResult.overall === 'amber' ? '#F4D8A8' : '#FCA5A5',
                  }}>
                    {qaResult.overall.toUpperCase()} · {qaResult.period}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-1.5">
                    {(qaResult.checks || []).map((c, i) => (
                      <div key={i} className="px-2 py-1.5 rounded"
                           style={{ background: '#0F1A2D', border: '1px solid #3A4A6A' }}>
                        <div style={{ color: '#9CA3AF', fontSize: 10 }}>{c.name}</div>
                        <div className="font-bold" style={{
                          color: c.gate === 'green' ? '#A0E5C2' :
                                 c.gate === 'amber' ? '#F4D8A8' : '#FCA5A5',
                        }}>{c.value === null ? '—' : c.value}</div>
                      </div>
                    ))}
                  </div>
                  {(qaResult.recommended_actions || []).length > 0 && (
                    <ul className="text-[10.5px] mt-1" style={{ color: '#B7C2D8' }}>
                      {qaResult.recommended_actions.map((a, i) => (
                        <li key={i}>· {a}</li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </div>
          )}

          {deckMsg && (
            <div className="text-[11px]" style={{ color: deckMsg.ok ? '#A0E5C2' : '#FCA5A5' }}
                 data-testid="ci-deck-msg">
              {deckMsg.text}
            </div>
          )}
        </div>
      )}

      {/* Recent Decks — lets the team re-download a deck without
         re-running Claude. Keeps the last 10 jobs with links. */}
      {isCeo && recentDecks.length > 0 && (
        <div className="text-[11px] p-3 rounded-md space-y-2"
             style={{ background: '#FFFEFB', border: '1px solid #E5E1DC' }}
             data-testid="ci-recent-decks">
          <div className="flex items-center justify-between">
            <div className="font-medium inline-flex items-center gap-2" style={{ color: '#1B2A4A' }}>
              <Clock size={12} /> Recent decks · {recentDecks.length}
            </div>
            <button onClick={loadRecentDecks}
                    className="text-[10.5px] px-2 py-0.5 rounded border hover:bg-white"
                    style={{ borderColor: '#E5E1DC', color: '#6B7280' }}
                    data-testid="ci-recent-decks-refresh">
              <RefreshCw size={10} className="inline" /> refresh
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]" style={{ color: '#1B2A4A' }}>
              <thead style={{ color: '#6B7280', background: '#F7F5F2' }}>
                <tr>
                  <th className="text-left px-2 py-1 font-medium">Period</th>
                  <th className="text-left px-2 py-1 font-medium">Generated</th>
                  <th className="text-left px-2 py-1 font-medium">By</th>
                  <th className="text-left px-2 py-1 font-medium">Status</th>
                  <th className="text-left px-2 py-1 font-medium">Size</th>
                  <th className="text-left px-2 py-1 font-medium">Pillars</th>
                  <th className="text-right px-2 py-1 font-medium">Action</th>
                </tr>
              </thead>
              <tbody>
                {recentDecks.map((d) => {
                  const when = d.started_at ? new Date(d.started_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—';
                  const ready = d.status === 'ready';
                  const sizeKb = d.size_bytes ? `${(d.size_bytes/1024).toFixed(0)} KB` : '—';
                  return (
                    <tr key={d.job_id} className="border-t" style={{ borderColor: '#F0EEE9' }}>
                      <td className="px-2 py-1.5">{d.period || d.month || '—'}</td>
                      <td className="px-2 py-1.5" style={{ color: '#6B7280' }}>{when}</td>
                      <td className="px-2 py-1.5" style={{ color: '#6B7280' }}>{(d.started_by || '').split('@')[0]}</td>
                      <td className="px-2 py-1.5">
                        <span className="inline-block px-1.5 py-0.5 rounded text-[10px]"
                              style={{
                                background: ready ? '#ECFDF5' : (d.status === 'failed' ? '#FEE2E2' : '#FEF3C7'),
                                color: ready ? '#1f5736' : (d.status === 'failed' ? '#7a1f1f' : '#78350F'),
                              }}>
                          {d.status}
                        </span>
                      </td>
                      <td className="px-2 py-1.5" style={{ color: '#6B7280' }}>{sizeKb}</td>
                      <td className="px-2 py-1.5" style={{ color: '#6B7280' }}>
                        {(d.pillars_used || []).join(', ') || '—'}
                      </td>
                      <td className="px-2 py-1.5 text-right">
                        {ready ? (
                          <button onClick={() => downloadRecentDeck(d)}
                                  data-testid={`ci-recent-deck-download-${d.job_id.slice(0,8)}`}
                                  className="text-[11px] px-2 py-0.5 rounded text-white hover:opacity-90"
                                  style={{ background: '#1C304A' }}>
                            Download
                          </button>
                        ) : <span style={{ color: '#9CA3AF' }}>—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* MCE Effectiveness — tracked SEPARATELY from customer engagement
         so April numbers in the client deck stay clean. Per Dipali
         30-Apr-2026: "I want to see what the engine is doing, but it
         must never inflate the client's engagement number." */}
      {isCeo && mceEff && mceEff.total_sent > 0 && (
        <div className="text-[11px] p-3 rounded-md space-y-2"
             style={{ background: '#FFF8EE', border: '1px solid #F4D8A8' }}
             data-testid="ci-mce-effectiveness">
          <div className="flex items-center justify-between">
            <div className="font-medium inline-flex items-center gap-2" style={{ color: '#8A4B00' }}>
              <MessageCircle size={12} /> MCE Effectiveness · last {mceEff.lookback_months} months
            </div>
            <div className="flex items-center gap-2">
              <a href={`/member-care/workbench/${clientId}`}
                 data-testid="ci-mce-workbench-link"
                 className="text-[10.5px] px-2 py-0.5 rounded font-medium hover:opacity-90"
                 style={{ background: '#1B2A4A', color: '#FFFFFF' }}>
                Open Workbench →
              </a>
              <span className="text-[10.5px]" style={{ color: '#6B5439' }}>
                Tracked separately · NOT counted in client engagement
              </span>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <div className="p-2 rounded bg-white" style={{ border: '1px solid #F0E5D0' }}>
              <div className="text-[10px] uppercase tracking-wide" style={{ color: '#9CA3AF' }}>Total nudges</div>
              <div className="font-semibold" style={{ color: '#1B2A4A' }}>{fmtN(mceEff.total_sent)}</div>
            </div>
            <div className="p-2 rounded bg-white" style={{ border: '1px solid #F0E5D0' }}>
              <div className="text-[10px] uppercase tracking-wide" style={{ color: '#9CA3AF' }}>Replied / actioned</div>
              <div className="font-semibold" style={{ color: '#1B2A4A' }}>{fmtN(mceEff.replied)}</div>
            </div>
            <div className="p-2 rounded bg-white" style={{ border: '1px solid #F0E5D0' }}>
              <div className="text-[10px] uppercase tracking-wide" style={{ color: '#9CA3AF' }}>Reply rate</div>
              <div className="font-semibold" style={{ color: mceEff.reply_rate_pct >= 20 ? '#2D8B5E' : '#8A4B00' }}>
                {fmtPct(mceEff.reply_rate_pct)}
              </div>
            </div>
            <div className="p-2 rounded bg-white" style={{ border: '1px solid #F0E5D0' }}>
              <div className="text-[10px] uppercase tracking-wide" style={{ color: '#9CA3AF' }}>Top trigger</div>
              <div className="font-semibold" style={{ color: '#1B2A4A' }}>
                {(mceEff.by_trigger?.[0]?.trigger || '—').replace(/_/g, ' ')}
              </div>
            </div>
          </div>
          {mceEff.by_trigger?.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {mceEff.by_trigger.slice(0, 8).map((t) => (
                <span key={t.trigger}
                      className="text-[10.5px] px-2 py-0.5 rounded"
                      style={{ background: '#FFFFFF', border: '1px solid #F0E5D0', color: '#6B5439' }}>
                  {(t.trigger || 'unknown').replace(/_/g, ' ')} · {fmtN(t.sent)}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Hero tiles — hover each to see breakdown via InfoTooltip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <TileWithTooltip
          breakdowns={breakdowns} kind="total_members"
          icon={Users} label="Total members"
          value={fmtN(a.total_members)}
          sub={`${fmtN(a.active_members)} active · ${fmtPct(a.active_pct)}`} />
        <TileWithTooltip
          breakdowns={breakdowns} kind="requests_this_month"
          icon={Activity} label={`Requests ${pack.anchor_month}`}
          value={fmtN(p.monthly_totals?.[pack.anchor_month] || 0)}
          sub={b.top_benefit ? `Top benefit · ${b.top_benefit}` : `${p.rows.length} categories used`} />
        <TileWithTooltip
          breakdowns={breakdowns} kind="top_benefit"
          icon={Sparkles} label={b.top_benefit ? "Top benefit" : "Top category"}
          value={b.top_benefit || p.top_pillar || '—'}
          sub={b.top_benefit ? `${fmtN((b.rows[0]||{}).total)} requests · last 6mo` : (p.top_pillar ? `${fmtN((p.rows[0]||{}).total)} requests · last 6mo` : 'No data yet')} />
        <TileWithTooltip
          breakdowns={breakdowns} kind="top_city"
          icon={MapPin} label="Top city"
          value={(g.top_10?.[0]?.city) || '—'}
          sub={g.total_cities ? `${g.total_cities} cities · top-3 = ${fmtPct(g.concentration?.top_3_pct)}` : 'No data yet'} />
      </div>

      {/* Activation depth — each box hover-opens a bucket tooltip */}
      <Section title="Activation Depth">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <DepthBoxWithTooltip breakdowns={breakdowns} kind="once_only"    label="Once only"  value={a.once_only}    pct={a.total_members ? (a.once_only/a.total_members*100) : 0}    color="#B0B7BB" />
          <DepthBoxWithTooltip breakdowns={breakdowns} kind="twice"        label="Twice"      value={a.twice_only}   pct={a.total_members ? (a.twice_only/a.total_members*100) : 0}   color="#5C5652" />
          <DepthBoxWithTooltip breakdowns={breakdowns} kind="thrice_plus"  label="3+ times"   value={a.thrice_plus}  pct={a.total_members ? (a.thrice_plus/a.total_members*100) : 0}  color="#2D8B5E" sub={`${fmtN(a.thrice_plus_requests)} reqs`} />
          <DepthBoxWithTooltip breakdowns={breakdowns} kind="inactive"     label="Inactive"   value={a.inactive}     pct={a.total_members ? (a.inactive/a.total_members*100) : 0}     color="#A32D2D" />
        </div>
      </Section>

      {/* Benefit summary table — top of taxonomy (post-Jan-2026 JLR migration). */}
      {b.rows.length > 0 && (
        <Section title={`Benefit Demand · last 6 months${b.coverage_pct ? ` · ${b.coverage_pct}% of tickets tagged` : ''}`}>
          <div className="overflow-x-auto" data-testid="ci-benefit-table">
            <table className="w-full text-[12px]">
              <thead><tr className="text-left" style={{ color: '#6B7280' }}>
                <th className="py-1.5 pr-3">Benefit</th>
                {b.months.map(m => <th key={m} className="py-1.5 px-2 text-right">{m.slice(2)}</th>)}
                <th className="py-1.5 pl-2 text-right font-semibold">Total</th>
              </tr></thead>
              <tbody>
                {b.rows.map(r => (
                  <tr key={r.benefit} className="border-t" style={{ borderColor: '#F0EEEA' }}>
                    <td className="py-1.5 pr-3 font-medium" style={{ color: '#1B2A4A' }}>{r.benefit}</td>
                    {b.months.map(m => <td key={m} className="py-1.5 px-2 text-right tabular-nums">{r.by_month[m] || ''}</td>)}
                    <td className="py-1.5 pl-2 text-right font-semibold tabular-nums" style={{ color: '#1C304A' }}>{r.total}</td>
                  </tr>
                ))}
                <tr className="border-t-2" style={{ borderColor: '#1C304A', background: '#F7F5F2' }}>
                  <td className="py-1.5 pr-3 font-bold">Total</td>
                  {b.months.map(m => <td key={m} className="py-1.5 px-2 text-right font-bold tabular-nums">{b.monthly_totals?.[m] || ''}</td>)}
                  <td className="py-1.5 pl-2 text-right font-bold tabular-nums" style={{ color: '#1C304A' }}>{b.grand_total}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="text-[10.5px] mt-1.5" style={{ color: '#9CA3AF' }}>
            Benefit is the top-level Zoho Desk taxonomy used from Jan 2026. Tickets logged before Jan show up under "Category" below.
          </div>
        </Section>
      )}

      {/* Pillar (= Zoho Desk's Category) — second level of the taxonomy. */}
      <Section title="Category · last 6 months">
        {p.rows.length === 0 ? (
          <Empty>No tickets synced yet. Click "Sync now" or "A · Apr 2026" to pull live Zoho data.</Empty>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead><tr className="text-left" style={{ color: '#6B7280' }}>
                <th className="py-1.5 pr-3">Category</th>
                {p.months.map(m => <th key={m} className="py-1.5 px-2 text-right">{m.slice(2)}</th>)}
                <th className="py-1.5 pl-2 text-right font-semibold">Total</th>
              </tr></thead>
              <tbody>
                {p.rows.map(r => (
                  <tr key={r.pillar} className="border-t" style={{ borderColor: '#F0EEEA' }}>
                    <td className="py-1.5 pr-3 font-medium" style={{ color: '#1B2A4A' }}>{r.pillar}</td>
                    {p.months.map(m => <td key={m} className="py-1.5 px-2 text-right tabular-nums">{r.by_month[m] || ''}</td>)}
                    <td className="py-1.5 pl-2 text-right font-semibold tabular-nums" style={{ color: '#1C304A' }}>{r.total}</td>
                  </tr>
                ))}
                <tr className="border-t-2" style={{ borderColor: '#1C304A', background: '#F7F5F2' }}>
                  <td className="py-1.5 pr-3 font-bold">Total</td>
                  {p.months.map(m => <td key={m} className="py-1.5 px-2 text-right font-bold tabular-nums">{p.monthly_totals?.[m] || ''}</td>)}
                  <td className="py-1.5 pl-2 text-right font-bold tabular-nums" style={{ color: '#1C304A' }}>{p.grand_total}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Tier (RR/RRS) */}
        <Section title="Tier · RR vs RRS">
          {t.tiers.length === 0 ? <Empty>No tier data.</Empty> : t.tiers.map(ti => (
            <div key={ti.tier} className="mb-3 last:mb-0">
              <div className="flex items-center justify-between text-[12px]">
                <span className="font-semibold" style={{ color: '#1B2A4A' }}>
                  <span className="inline-block w-2 h-2 rounded-full mr-2" style={{ background: TIER_DOT[ti.tier] || '#B0B7BB' }} />
                  {ti.tier}
                </span>
                <span style={{ color: '#6B7280' }}>{fmtN(ti.requests)} reqs · {fmtN(ti.unique_members)} members · {ti.requests_per_member}/member</span>
              </div>
              {ti.pillar_mix.length > 0 && (
                <div className="text-[11px] mt-1 truncate" style={{ color: '#9CA3AF' }}>
                  {ti.pillar_mix.slice(0,5).map(pm => `${pm.pillar} ${pm.requests}`).join(' · ')}
                </div>
              )}
            </div>
          ))}
        </Section>

        {/* Geography */}
        <Section title={`Top cities · ${pack.anchor_month}`}>
          {g.top_10.length === 0 ? <Empty>No geography data.</Empty> : (
            <div className="space-y-1.5">
              {g.top_10.slice(0, 8).map((c, i) => (
                <div key={c.city} className="flex items-center gap-2 text-[12px]">
                  <span className="text-[10px] tabular-nums" style={{ color: '#9CA3AF', width: 16 }}>{i+1}.</span>
                  <span className="flex-1 truncate" style={{ color: '#1B2A4A' }}>{c.city}</span>
                  <span className="tabular-nums" style={{ color: '#6B7280' }}>{fmtN(c.requests)}</span>
                  <span className="text-[10px] tabular-nums w-12 text-right" style={{ color: '#9CA3AF' }}>{fmtPct(c.pct)}</span>
                </div>
              ))}
              <div className="text-[10px] mt-2 italic" style={{ color: '#9CA3AF' }}>
                Top-3 cities = {fmtPct(g.concentration?.top_3_pct)} · top-10 = {fmtPct(g.concentration?.top_10_pct)}
              </div>
            </div>
          )}
        </Section>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Channel mix */}
        <Section title={`Channels · ${pack.anchor_month}`}>
          {ch.channels.length === 0 ? <Empty>No channel data.</Empty> : (
            <div className="space-y-2">
              {ch.channels.map(c => {
                const Icon = c.channel?.toLowerCase().includes('email') ? Mail
                           : c.channel?.toLowerCase().includes('whats') ? MessageCircle
                           : Layers;
                const pct = ch.grand_total ? (c.total / ch.grand_total * 100) : 0;
                return (
                  <div key={c.channel}>
                    <div className="flex items-center gap-2 text-[12px]">
                      <Icon size={13} style={{ color: '#2A7B88' }} />
                      <span className="font-medium" style={{ color: '#1B2A4A' }}>{c.channel}</span>
                      <span className="ml-auto tabular-nums" style={{ color: '#6B7280' }}>{fmtN(c.total)} · {pct.toFixed(0)}%</span>
                    </div>
                    <div className="h-1.5 rounded-full mt-1" style={{ background: '#F0EEEA' }}>
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, background: '#2A7B88' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Section>

        {/* Monthly trend */}
        <Section title="Onboarding vs Usage · 6mo">
          {tr.series.length === 0 ? <Empty>No data.</Empty> : (
            <div className="space-y-1">
              {tr.series.map(s => {
                const max = Math.max(1, ...tr.series.map(x => Math.max(x.onboarded, x.requests)));
                return (
                  <div key={s.month} className="grid grid-cols-12 gap-2 text-[11px] items-center">
                    <span className="col-span-2" style={{ color: '#6B7280' }}>{s.month.slice(2)}</span>
                    <span className="col-span-2 text-right tabular-nums" style={{ color: '#5C5652' }}>{fmtN(s.onboarded)}</span>
                    <div className="col-span-3 h-2 rounded" style={{ background: '#F0EEEA' }}>
                      <div className="h-full rounded" style={{ width: `${s.onboarded/max*100}%`, background: '#5C5652' }} />
                    </div>
                    <span className="col-span-2 text-right tabular-nums" style={{ color: '#2D8B5E' }}>{fmtN(s.requests)}</span>
                    <div className="col-span-3 h-2 rounded" style={{ background: '#F0EEEA' }}>
                      <div className="h-full rounded" style={{ width: `${s.requests/max*100}%`, background: '#2D8B5E' }} />
                    </div>
                  </div>
                );
              })}
              <div className="flex items-center gap-3 text-[10px] pt-2" style={{ color: '#9CA3AF' }}>
                <span><span className="inline-block w-2 h-2 mr-1" style={{ background: '#5C5652' }} />Onboarded</span>
                <span><span className="inline-block w-2 h-2 mr-1" style={{ background: '#2D8B5E' }} />Requests</span>
              </div>
            </div>
          )}
        </Section>
      </div>

      {/* Layer 1 · Live Tickets feed */}
      <Section title={
        <span className="inline-flex items-center gap-2">
          <Inbox size={13} /> Live Tickets · last {ticketLimit}
          {ticketsLoading && <RefreshCw size={11} className="animate-spin" />}
        </span>
      }>
        {/* Quick-range chips + date inputs */}
        <TicketDateRange
          start={ticketStart} end={ticketEnd}
          onChange={(s, e) => { setTicketStart(s); setTicketEnd(e); }}
        />
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <select
            value={ticketLimit}
            onChange={(e) => setTicketLimit(Number(e.target.value))}
            className="text-[11px] px-2 py-1 rounded border bg-white"
            style={{ borderColor: '#E5E1DC', color: '#1B2A4A' }}
            data-testid="ci-ticket-limit">
            <option value={20}>20 / page</option>
            <option value={50}>50 / page</option>
            <option value={100}>100 / page</option>
            <option value={200}>200 / page</option>
          </select>
          <select
            value={ticketStatus}
            onChange={(e) => setTicketStatus(e.target.value)}
            className="text-[11px] px-2 py-1 rounded border bg-white"
            style={{ borderColor: '#E5E1DC', color: '#1B2A4A' }}
            data-testid="ci-ticket-status-filter">
            <option value="">All statuses</option>
            <option value="Open">Open</option>
            <option value="Closed">Closed</option>
            <option value="Confirmed">Confirmed</option>
            <option value="InProcess">In Process</option>
          </select>
          <button onClick={loadTickets} disabled={ticketsLoading}
            className="text-[11px] px-2 py-1 rounded border hover:bg-gray-50 inline-flex items-center gap-1 disabled:opacity-50"
            style={{ borderColor: '#E5E1DC', color: '#1B2A4A' }}
            data-testid="ci-tickets-refresh">
            <RefreshCw size={11} className={ticketsLoading ? 'animate-spin' : ''} /> Refresh
          </button>
          <TicketExportButtons
            clientId={clientId}
            params={{ status: ticketStatus, start: ticketStart, end: ticketEnd }}
            disabled={ticketsTotal === 0}
            total={ticketsTotal}
          />
          <span className="text-[10px] ml-auto" style={{ color: '#9CA3AF' }}>
            {ticketsTotal > 0 ? (
              <>
                Showing <strong style={{ color: '#1B2A4A' }}>{ticketOffset + 1}-{Math.min(ticketOffset + tickets.length, ticketsTotal)}</strong>
                {' of '}<strong style={{ color: '#1B2A4A' }}>{ticketsTotal.toLocaleString('en-IN')}</strong> · newest first
              </>
            ) : (
              <>{tickets.length} tickets · newest first</>
            )}
          </span>
          <TicketPaginator
            total={ticketsTotal}
            offset={ticketOffset}
            limit={ticketLimit}
            onChange={setTicketOffset}
            disabled={ticketsLoading}
          />
        </div>
        {tickets.length === 0 ? (
          <Empty>No tickets yet. Run a sync to pull live Zoho tickets, or check that the OAuth consent is granted.</Empty>
        ) : (
          <div className="overflow-x-auto" style={{ maxHeight: 480 }}>
            <table className="w-full text-[11px]">
              <thead className="sticky top-0" style={{ background: '#F7F5F2' }}>
                <tr className="text-left" style={{ color: '#6B7280' }}>
                  <th className="py-1.5 pr-2">#</th>
                  <th className="py-1.5 px-2">Date</th>
                  <th className="py-1.5 px-2">Pillar</th>
                  <th className="py-1.5 px-2">Member</th>
                  <th className="py-1.5 px-2">City</th>
                  <th className="py-1.5 px-2">Tier</th>
                  <th className="py-1.5 px-2">Subject</th>
                  <th className="py-1.5 pl-2">Status</th>
                </tr>
              </thead>
              <tbody data-testid="ci-tickets-tbody">
                {tickets.map(t => (
                  <tr key={t.zoho_ticket_id} className="border-t hover:bg-gray-50" style={{ borderColor: '#F0EEEA' }}>
                    <td className="py-1.5 pr-2 tabular-nums" style={{ color: '#1B2A4A' }}>{t.ticket_number || '—'}</td>
                    <td className="py-1.5 px-2 tabular-nums" style={{ color: '#6B7280' }}>{(t.created_date || '').slice(0, 10) || '—'}</td>
                    <td className="py-1.5 px-2" style={{ color: '#2A7B88' }}>{t.pillar || '—'}</td>
                    <td className="py-1.5 px-2 truncate" style={{ color: '#1B2A4A', maxWidth: 140 }}>{t.member_name || '—'}</td>
                    <td className="py-1.5 px-2" style={{ color: '#6B7280' }}>{t.city || '—'}</td>
                    <td className="py-1.5 px-2 font-semibold" style={{ color: TIER_DOT[t.member_tier] || '#9CA3AF' }}>{t.member_tier || '—'}</td>
                    <td className="py-1.5 px-2 truncate" style={{ color: '#1B2A4A', maxWidth: 280 }} title={t.subject}>{t.subject || '—'}</td>
                    <td className="py-1.5 pl-2">
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-medium" style={{
                        background: t.is_active_touch ? '#FEF3C7' : '#E5F4ED',
                        color: t.is_active_touch ? '#92400E' : '#1f5736',
                      }}>{t.status || '—'}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* Layer 3 · Report Generator (Phase 6 stub) */}
      {isCeo && (
        <Section title={
          <span className="inline-flex items-center gap-2" id="ci-report-generator">
            <FileText size={13} /> Metric snapshot · verify before deck
          </span>
        }>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-wider mb-1" style={{ color: '#9CA3AF' }}>Start date</label>
              <div className="inline-flex items-center gap-1 px-2 py-1 rounded border bg-white" style={{ borderColor: '#E5E1DC' }}>
                <Calendar size={11} style={{ color: '#9CA3AF' }} />
                <input type="date" value={reportStart} onChange={(e) => setReportStart(e.target.value)}
                  className="text-[12px] outline-none bg-transparent" data-testid="ci-report-start" />
              </div>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wider mb-1" style={{ color: '#9CA3AF' }}>End date</label>
              <div className="inline-flex items-center gap-1 px-2 py-1 rounded border bg-white" style={{ borderColor: '#E5E1DC' }}>
                <Calendar size={11} style={{ color: '#9CA3AF' }} />
                <input type="date" value={reportEnd} onChange={(e) => setReportEnd(e.target.value)}
                  className="text-[12px] outline-none bg-transparent" data-testid="ci-report-end" />
              </div>
            </div>
            <div className="flex flex-wrap gap-1">
              {[
                { label: 'Apr 2026', s: '2026-04-01', e: '2026-04-30' },
                { label: 'Mar 2026', s: '2026-03-01', e: '2026-03-31' },
                { label: 'Q1 2026', s: '2026-01-01', e: '2026-03-31' },
                { label: 'H2 2025', s: '2025-07-01', e: '2025-12-31' },
                { label: 'Since Jun 2025', s: '2025-06-01', e: '2026-04-30' },
              ].map(p => (
                <button key={p.label}
                  onClick={() => { setReportStart(p.s); setReportEnd(p.e); }}
                  className="text-[10px] px-2 py-1 rounded border hover:bg-white"
                  style={{ borderColor: '#E5E1DC', color: '#1B2A4A', background: '#F7F5F2' }}
                  data-testid={`ci-report-preset-${p.label.replace(/\s/g, '-').toLowerCase()}`}>
                  {p.label}
                </button>
              ))}
            </div>
            <button onClick={generateReport} disabled={reportBusy}
              className="px-3 py-1.5 rounded-md text-[11px] font-medium text-white inline-flex items-center gap-1 disabled:opacity-50"
              style={{ backgroundColor: '#1C304A' }}
              data-testid="ci-report-generate-btn">
              <FileText size={12} /> {reportBusy ? 'Building…' : 'Build Report'}
            </button>
          </div>
          {reportMsg && (
            <div className="mt-3 text-[12px] px-3 py-2 rounded-md" style={{
              background: reportMsg.ok ? '#ECFDF5' : '#FEE2E2',
              color: reportMsg.ok ? '#1f5736' : '#7a1f1f',
            }} data-testid="ci-report-msg">
              {reportMsg.text}
            </div>
          )}
          <p className="text-[10px] mt-2 italic" style={{ color: '#9CA3AF' }}>
            Phase 6 builds the full 27-slide PowerPoint with Claude narrative (scroll up to the <strong>Member Experience Deck</strong> panel). This panel returns a metric snapshot so Dipali can sanity-check the numbers against Nisha's before the deck runs.
          </p>
        </Section>
      )}
      </>)}

      {/* Deck preview — full-screen modal that drafts narratives,
         lets Dipali edit inline, then finalises to PPT. Rendered
         here at the root so it covers the entire dashboard. */}
      {showPreview && (
        <DeckPreview
          clientId={clientId}
          month={deckMode === 'month' ? deckMonth : null}
          start={deckMode === 'range' ? deckStart : null}
          end={deckMode === 'range' ? deckEnd : null}
          onClose={() => { setShowPreview(false); loadRecentDecks(); }}
          onFinalized={() => loadRecentDecks()}
        />
      )}
    </div>
  );
}

function TabButton({ active, onClick, testId, icon, children }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className="px-3 py-2 text-[12px] font-medium inline-flex items-center gap-1.5 transition border-b-2"
      style={{
        borderColor: active ? '#1C304A' : 'transparent',
        color: active ? '#1B2A4A' : '#6B7280',
        marginBottom: -1,
      }}>
      {icon} {children}
    </button>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white rounded-xl border p-4" style={{ borderColor: '#E5E1DC' }}>
      <div className="text-[11px] font-bold uppercase tracking-wider mb-3" style={{ color: '#2A7B88' }}>{title}</div>
      {children}
    </div>
  );
}

function Tile({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-white rounded-xl border p-4" style={{ borderColor: '#E5E1DC' }}>
      <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider" style={{ color: '#9CA3AF' }}>
        <Icon size={12} /> {label}
      </div>
      <div className="text-2xl font-bold mt-1 truncate" style={{ color: '#1B2A4A' }}>{value}</div>
      {sub && <div className="text-[11px] mt-0.5" style={{ color: '#6B7280' }}>{sub}</div>}
    </div>
  );
}

function DepthBox({ label, value, pct, color, sub }) {
  return (
    <div className="rounded-lg p-3 border" style={{ borderColor: '#E5E1DC' }}>
      <div className="text-[10px] uppercase tracking-wider" style={{ color: '#9CA3AF' }}>{label}</div>
      <div className="text-xl font-bold mt-0.5 tabular-nums" style={{ color: '#1B2A4A' }}>{fmtN(value)}</div>
      <div className="h-1 rounded-full mt-2" style={{ background: '#F0EEEA' }}>
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
      </div>
      <div className="text-[10px] mt-1 tabular-nums" style={{ color: '#9CA3AF' }}>{pct.toFixed(1)}%{sub ? ` · ${sub}` : ''}</div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Stat cards wrapped in an InfoTooltip (hover/tap → breakdown popup).
// All tooltip payloads are built from /tooltip-breakdowns.
// ──────────────────────────────────────────────────────────────────────────
function TileWithTooltip({ breakdowns, kind, icon, label, value, sub }) {
  const tt = buildTopTileTooltip(breakdowns, kind);
  return (
    <InfoTooltip
      trigger={<Tile icon={icon} label={label} value={value} sub={sub} />}
      title={tt.title}
      subtitle={tt.subtitle}
      sections={tt.sections}
      footnote={tt.footnote}
      width={340}
      testId={`ci-tooltip-${kind}`}
    />
  );
}

function DepthBoxWithTooltip({ breakdowns, kind, label, value, pct, color, sub }) {
  const tt = buildDepthTooltip(breakdowns, kind, value);
  return (
    <InfoTooltip
      trigger={<DepthBox label={label} value={value} pct={pct} color={color} sub={sub} />}
      title={tt.title}
      subtitle={tt.subtitle}
      sections={tt.sections}
      footnote={tt.footnote}
      width={320}
      testId={`ci-tooltip-depth-${kind}`}
    />
  );
}

function buildTopTileTooltip(breakdowns, kind) {
  if (!breakdowns) {
    return { title: 'Loading…', subtitle: 'Fetching breakdown', sections: [] };
  }
  if (kind === 'total_members') {
    const tm = breakdowns.total_members || {};
    const total = tm.total_all || 1;
    return {
      title: 'Total members — how it breaks down',
      subtitle: `${(total).toLocaleString('en-IN')} total rows in zoho_contacts for this client`,
      sections: [
        {
          label: 'By type × status',
          rows: (tm.by_type_status || []).map(r => ({
            label: r.label, note: r.note, value: r.count,
            pct: total ? (100 * r.count / total) : 0,
          })),
        },
        {
          label: 'Primary+Active by tier',
          rows: (tm.by_tier || []).map(r => ({
            label: r.tier, value: r.count,
            pct: tm.by_type_status?.[0]?.count ? (100 * r.count / tm.by_type_status[0].count) : 0,
          })),
        },
      ],
      footnote: 'Primary+Active is the universe all other charts use.',
    };
  }
  if (kind === 'requests_this_month') {
    const cm = breakdowns.current_month_requests || {};
    const total = cm.total || 1;
    return {
      title: `Requests · ${cm.month || 'this month'}`,
      subtitle: `${(cm.total || 0).toLocaleString('en-IN')} tickets raised in this month`,
      sections: [
        {
          label: 'By benefit (pillar)',
          rows: (cm.by_pillar || []).slice(0, 15).map(r => ({
            label: r.benefit, value: r.total,
            pct: total ? (100 * r.total / total) : 0,
          })),
        },
        {
          label: 'By ticket status',
          rows: (cm.by_status || []).map(r => ({
            label: r.status, value: r.count,
            pct: total ? (100 * r.count / total) : 0,
          })),
        },
      ],
    };
  }
  if (kind === 'top_benefit') {
    const tb = breakdowns.top_benefit_ranking || {};
    const total = tb.grand_total || 1;
    return {
      title: 'All benefits · last 6 months',
      subtitle: `${(tb.grand_total || 0).toLocaleString('en-IN')} requests across ${tb.rows?.length || 0} benefits · ${tb.coverage_pct || 0}% tagged`,
      sections: [
        {
          label: 'Full ranking (every pillar)',
          rows: (tb.rows || []).map(r => ({
            label: r.benefit, value: r.total,
            pct: total ? (100 * r.total / total) : 0,
          })),
        },
      ],
      footnote: '% based on all tagged requests in the last 6 months.',
    };
  }
  if (kind === 'top_city') {
    const tc = breakdowns.top_cities_ranking || {};
    const total = tc.total_requests || 1;
    return {
      title: 'Top 10 cities',
      subtitle: `${(tc.total_requests || 0).toLocaleString('en-IN')} requests across ${tc.total_cities || 0} cities · ${(tc.concentration?.top_10_pct || 0).toFixed(1)}% in top 10`,
      sections: [
        {
          label: 'By request volume',
          rows: (tc.top_10 || []).map(r => ({
            label: r.city || 'Unknown',
            value: r.requests,
            pct: total ? (100 * r.requests / total) : 0,
          })),
        },
      ],
      footnote: tc.unknown_requests
        ? `${tc.unknown_requests.toLocaleString('en-IN')} requests have no city captured — ticket form or CRM record incomplete.`
        : undefined,
    };
  }
  return { title: '—', sections: [] };
}

function buildDepthTooltip(breakdowns, kind, total) {
  if (!breakdowns?.activation_depth) {
    return { title: 'Loading…', subtitle: 'Fetching breakdown', sections: [] };
  }
  const bucket = breakdowns.activation_depth[kind] || {};
  const labelMap = {
    once_only: 'Once only',
    twice: 'Twice',
    thrice_plus: '3+ times',
    inactive: 'Inactive',
  };
  const titleLabel = labelMap[kind] || kind;
  const sections = [
    {
      label: 'Top 5 cities',
      rows: (bucket.top_cities || []).map(r => ({
        label: r.city || 'Unknown', value: r.count,
        pct: total ? (100 * r.count / total) : 0,
      })),
    },
    {
      label: 'Top 5 benefits they ask for',
      rows: (bucket.top_benefits || []).map(r => ({
        label: r.benefit, value: r.count,
      })),
    },
  ];
  if (bucket.super_users) {
    sections.push({
      label: 'Top 5 super users (by request count)',
      rows: bucket.super_users.map(r => ({
        label: r.name || '(unknown)', value: r.request_count,
      })),
    });
  }
  let footnote;
  if (kind === 'thrice_plus' && bucket.avg_requests) {
    footnote = `${bucket.avg_requests.toFixed(1)} avg requests/member · ${(bucket.total_requests || 0).toLocaleString('en-IN')} total requests in this bucket.`;
  }
  if (kind === 'inactive') {
    if (bucket.top_lsms?.length) {
      sections.push({
        label: 'Their LSMs',
        rows: bucket.top_lsms.map(r => ({
          label: r.lsm || 'Unassigned', value: r.count,
        })),
      });
    }
    footnote = `${bucket.joined_last_30d || 0} of these joined in the last 30 days (just-joined vs churned).`;
  }
  return {
    title: `${titleLabel} · ${(total || 0).toLocaleString('en-IN')} members`,
    subtitle: kind === 'inactive'
      ? 'Primary+Active members with zero tickets in CRM'
      : 'Primary+Active members matched via email bridge',
    sections,
    footnote,
  };
}

function Empty({ children }) {
  return <p className="text-[12px] py-2" style={{ color: '#9CA3AF' }}>{children}</p>;
}


// ────────────────────────────────────────────────────────────────────────────
// ReconciliationBadge — green/amber/grey indicator on the dashboard header.
// Pings GET /soul-engine/{client_id}/reconciliation (cached 5 min).
// Click to refresh and see the live drift number.
// ────────────────────────────────────────────────────────────────────────────
function ReconciliationBadge({ clientId }) {
  const [state, setState] = React.useState({ status: 'loading' });
  const [busy, setBusy] = React.useState(false);

  const load = React.useCallback(async (fresh = false) => {
    setBusy(true);
    try {
      const r = await api.get(`/soul-engine/${clientId}/reconciliation`,
        { params: fresh ? { fresh: true } : {} });
      setState(r.data || { status: 'unknown' });
    } catch {
      setState({ status: 'unknown', message: 'Reconciliation unavailable' });
    } finally { setBusy(false); }
  }, [clientId]);

  React.useEffect(() => { load(false); }, [load]);

  if (state.status === 'loading') {
    return (
      <div className="text-[10px] mt-1 inline-flex items-center gap-1.5"
           style={{ color: '#9CA3AF' }}>
        <span className="w-1.5 h-1.5 rounded-full animate-pulse"
              style={{ background: '#9CA3AF' }} />
        Reconciling with Zoho…
      </div>
    );
  }

  const palette = {
    green:   { dot: '#1f5736', text: '#1f5736', bg: '#E8F4ED', border: '#B8DCC4' },
    amber:   { dot: '#A86B2C', text: '#A86B2C', bg: '#FBF1E0', border: '#F0D9A8' },
    unknown: { dot: '#9CA3AF', text: '#6B7280', bg: '#F4F2EE', border: '#E5E1DC' },
  }[state.status] || { dot: '#9CA3AF', text: '#6B7280', bg: '#F4F2EE', border: '#E5E1DC' };

  const label = state.status === 'green'
    ? `In sync · ${(state.scaleboard_count || 0).toLocaleString('en-IN')} match Zoho`
    : state.status === 'amber'
    ? `Drift detected · ${state.drift > 0 ? '+' : ''}${state.drift} vs Zoho`
    : 'Reconciliation unavailable';

  return (
    <button
      onClick={() => load(true)}
      disabled={busy}
      data-testid="ci-reconciliation-badge"
      title={state.message || 'Click to refresh from Zoho live'}
      className="text-[11px] mt-1 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border transition-all hover:opacity-90"
      style={{
        background: palette.bg, color: palette.text,
        borderColor: palette.border, fontWeight: 500,
      }}>
      <span className={`w-1.5 h-1.5 rounded-full ${busy ? 'animate-pulse' : ''}`}
            style={{ background: palette.dot }} />
      {busy ? 'Checking Zoho…' : label}
      {state.status === 'amber' && (
        <span className="text-[9px] opacity-70">· refresh CRM</span>
      )}
    </button>
  );
}


// ────────────────────────────────────────────────────────────────────────────
// SyncStatusPill — relative time + ticket count for the most recent sync.
// Polls /_meta/sync-runs every 30s so an in-progress sync surfaces the live
// "⏳ Syncing…" state.
// 07-Feb-2026 (Dipali doctrine): "Last sync: 2 min ago · ✅ 2096 tickets"
// ────────────────────────────────────────────────────────────────────────────
function SyncStatusPill({ lastSync, clientId }) {
  const [run, setRun] = React.useState(lastSync);

  const refresh = React.useCallback(async () => {
    try {
      const r = await api.get('/customer-intelligence/_meta/sync-runs',
        { params: { limit: 5 } });
      const latestForClient = (r.data?.runs || [])
        .find(x => x.client_id === clientId && x.mode !== 'csv_enrichment');
      if (latestForClient) {
        setRun({
          ts: latestForClient.ts,
          tickets_upserted: latestForClient.tickets_upserted
            ?? (latestForClient.result || {}).tickets_upserted
            ?? (latestForClient.result?.results?.[0]?.tickets_upserted)
            ?? 0,
          mode: latestForClient.mode,
          ok: latestForClient.ok,
          duration_s: latestForClient.duration_s,
          error: latestForClient.error,
        });
      }
    } catch { /* keep stale state */ }
  }, [clientId]);

  React.useEffect(() => {
    refresh();
    const t = setInterval(refresh, 30000);
    return () => clearInterval(t);
  }, [refresh]);

  if (!run || !run.ts) return null;

  const ageMs = Date.now() - new Date(run.ts).getTime();
  const ageMin = Math.round(ageMs / 60000);
  const rel = ageMin < 1 ? 'just now'
    : ageMin < 60 ? `${ageMin} min ago`
    : ageMin < 60 * 24 ? `${Math.round(ageMin / 60)} hr ago`
    : `${Math.round(ageMin / 60 / 24)} d ago`;

  const isRunning = run.ok === null || run.ok === undefined;
  const isFailed = run.ok === false;

  const palette = isRunning
    ? { dot: '#A86B2C', text: '#A86B2C', bg: '#FBF1E0', border: '#F0D9A8' }
    : isFailed
    ? { dot: '#A32D2D', text: '#A32D2D', bg: '#FEE2E2', border: '#FCA5A5' }
    : { dot: '#1f5736', text: '#1f5736', bg: '#E8F4ED', border: '#B8DCC4' };

  const label = isRunning
    ? `⏳ Syncing… started ${rel}`
    : isFailed
    ? `❌ Sync failed ${rel} · ${run.error || 'see history'}`
    : `Last sync: ${rel} · ✅ ${(run.tickets_upserted || 0).toLocaleString('en-IN')} tickets`;

  const tooltip = isRunning
    ? 'Sync is running in the background. This pill auto-refreshes every 30s.'
    : isFailed
    ? `Failed sync at ${new Date(run.ts).toLocaleString('en-IN')}: ${run.error || 'see /_meta/sync-runs'}`
    : `Synced ${new Date(run.ts).toLocaleString('en-IN')}${run.duration_s ? ` in ${run.duration_s.toFixed(1)}s` : ''}`;

  return (
    <div
      data-testid="ci-sync-status-pill"
      title={tooltip}
      className="text-[11px] mt-1 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border"
      style={{
        background: palette.bg, color: palette.text,
        borderColor: palette.border, fontWeight: 500,
      }}>
      <span className={`w-1.5 h-1.5 rounded-full ${isRunning ? 'animate-pulse' : ''}`}
            style={{ background: palette.dot }} />
      {label}
    </div>
  );
}


// ─── Live Tickets toolbar helpers ─────────────────────────────────────────────

function _isoDaysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}
function _isoToday() { return new Date().toISOString().slice(0, 10); }
function _isoMonthStart(offset = 0) {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() + offset);
  return d.toISOString().slice(0, 10);
}
function _isoMonthEnd(offset = 0) {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() + offset + 1);
  d.setDate(0);
  return d.toISOString().slice(0, 10);
}

const _QUICK_RANGES = [
  { key: '7d',    label: 'Last 7d',     get: () => [_isoDaysAgo(7), _isoToday()] },
  { key: '30d',   label: 'Last 30d',    get: () => [_isoDaysAgo(30), _isoToday()] },
  { key: '90d',   label: 'Last 90d',    get: () => [_isoDaysAgo(90), _isoToday()] },
  { key: 'mtd',   label: 'This month',  get: () => [_isoMonthStart(0), _isoToday()] },
  { key: 'lm',    label: 'Last month',  get: () => [_isoMonthStart(-1), _isoMonthEnd(-1)] },
  { key: 'all',   label: 'All time',    get: () => ['', ''] },
];

function TicketDateRange({ start, end, onChange }) {
  const activeKey = (() => {
    for (const r of _QUICK_RANGES) {
      const [s, e] = r.get();
      if (s === start && e === end) return r.key;
    }
    return start || end ? 'custom' : 'all';
  })();
  return (
    <div className="flex items-center gap-2 mb-2 flex-wrap"
      data-testid="ci-tickets-daterange">
      <Calendar size={12} style={{ color: '#6B7280' }} />
      <span className="text-[10px] uppercase tracking-wider" style={{ color: '#6B7280' }}>Range:</span>
      {_QUICK_RANGES.map((r) => {
        const isActive = activeKey === r.key;
        return (
          <button key={r.key}
            onClick={() => { const [s, e] = r.get(); onChange(s, e); }}
            className="text-[11px] px-2 py-0.5 rounded-full border"
            style={{
              background: isActive ? '#1B2A4A' : 'white',
              color: isActive ? 'white' : '#1B2A4A',
              borderColor: isActive ? '#1B2A4A' : '#E5E1DC',
            }}
            data-testid={`ci-tickets-range-${r.key}`}>
            {r.label}
          </button>
        );
      })}
      <span className="ml-2 text-[10px]" style={{ color: '#9CA3AF' }}>or</span>
      <input type="date" value={start} max={end || undefined}
        onChange={(e) => onChange(e.target.value, end)}
        className="text-[11px] px-2 py-0.5 rounded border bg-white"
        style={{ borderColor: '#E5E1DC', color: '#1B2A4A' }}
        data-testid="ci-tickets-start" />
      <span className="text-[11px]" style={{ color: '#6B7280' }}>→</span>
      <input type="date" value={end} min={start || undefined}
        onChange={(e) => onChange(start, e.target.value)}
        className="text-[11px] px-2 py-0.5 rounded border bg-white"
        style={{ borderColor: '#E5E1DC', color: '#1B2A4A' }}
        data-testid="ci-tickets-end" />
      {(start || end) && (
        <button onClick={() => onChange('', '')}
          className="text-[10px] underline" style={{ color: '#6B7280' }}
          data-testid="ci-tickets-range-clear">
          clear
        </button>
      )}
    </div>
  );
}

function TicketExportButtons({ clientId, params, disabled, total }) {
  const [busy, setBusy] = useState(null);
  const download = async (fmt) => {
    setBusy(fmt);
    try {
      const url = new URL(
        `${process.env.REACT_APP_BACKEND_URL}/api/customer-intelligence/${clientId}/recent-tickets/export.${fmt}`,
      );
      Object.entries(params || {}).forEach(([k, v]) => { if (v) url.searchParams.set(k, v); });
      const r = await fetch(url.toString(), {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!r.ok) {
        const txt = await r.text().catch(() => '');
        throw new Error(txt || `HTTP ${r.status}`);
      }
      const blob = await r.blob();
      const cd = r.headers.get('Content-Disposition') || '';
      const m = cd.match(/filename=([^;]+)/i);
      const filename = m ? m[1].trim().replace(/"/g, '') : `${clientId}_tickets.${fmt}`;
      const a = document.createElement('a');
      a.href = window.URL.createObjectURL(blob);
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(a.href);
    } catch (e) {
      /* eslint-disable no-alert */
      alert(`Export failed: ${e.message || e}`);
    } finally { setBusy(null); }
  };
  const title = total > 0
    ? `Export ${total.toLocaleString('en-IN')} matching ticket${total === 1 ? '' : 's'} (capped at 50,000)`
    : 'No tickets to export';
  return (
    <>
      <button onClick={() => download('csv')} disabled={disabled || busy !== null}
        title={title}
        className="text-[11px] px-2 py-1 rounded border bg-white hover:bg-gray-50 inline-flex items-center gap-1 disabled:opacity-40"
        style={{ borderColor: '#E5E1DC', color: '#1B2A4A' }}
        data-testid="ci-tickets-export-csv">
        {busy === 'csv' ? <Loader2 size={11} className="animate-spin" /> : <FileText size={11} />}
        CSV
      </button>
      <button onClick={() => download('xlsx')} disabled={disabled || busy !== null}
        title={title}
        className="text-[11px] px-2 py-1 rounded border bg-white hover:bg-gray-50 inline-flex items-center gap-1 disabled:opacity-40"
        style={{ borderColor: '#E5E1DC', color: '#1B2A4A' }}
        data-testid="ci-tickets-export-xlsx">
        {busy === 'xlsx' ? <Loader2 size={11} className="animate-spin" /> : <FileText size={11} />}
        Excel
      </button>
    </>
  );
}

function TicketPaginator({ total, offset, limit, onChange, disabled }) {
  const pages = Math.max(1, Math.ceil((total || 0) / limit));
  const current = Math.floor(offset / limit) + 1;
  if (total === 0) return null;
  const wanted = new Set([1, pages, current, current - 1, current + 1, current - 2, current + 2]);
  const visible = [...wanted].filter((p) => p >= 1 && p <= pages).sort((a, b) => a - b);
  const items = [];
  let prev = 0;
  for (const p of visible) {
    if (p - prev > 1) items.push('…');
    items.push(p);
    prev = p;
  }
  const go = (p) => onChange(Math.max(0, (p - 1) * limit));
  return (
    <div className="inline-flex items-center gap-1" data-testid="ci-tickets-paginator">
      <button onClick={() => go(current - 1)} disabled={current === 1 || disabled}
        className="text-[11px] px-2 py-1 rounded border bg-white disabled:opacity-40"
        style={{ borderColor: '#E5E1DC', color: '#1B2A4A' }}
        data-testid="ci-tickets-prev">←</button>
      {items.map((it, i) => it === '…' ? (
        <span key={`e${i}`} className="text-[11px]" style={{ color: '#9CA3AF' }}>…</span>
      ) : (
        <button key={it} onClick={() => go(it)} disabled={disabled}
          className="text-[11px] px-2 py-1 rounded border min-w-[28px]"
          style={{
            background: it === current ? '#1B2A4A' : 'white',
            color: it === current ? 'white' : '#1B2A4A',
            borderColor: it === current ? '#1B2A4A' : '#E5E1DC',
          }}
          data-testid={`ci-tickets-page-${it}`}>
          {it}
        </button>
      ))}
      <button onClick={() => go(current + 1)} disabled={current === pages || disabled}
        className="text-[11px] px-2 py-1 rounded border bg-white disabled:opacity-40"
        style={{ borderColor: '#E5E1DC', color: '#1B2A4A' }}
        data-testid="ci-tickets-next">→</button>
    </div>
  );
}

